import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { Label } from "@/components/ui/label";
import { useState, useRef } from "react";
import { toast } from "@/hooks/use-toast";

const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

// Természetes sorrendezés (pl. content1, content2, content10)
const naturalSort = (a: string, b: string): number => {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};

const Translate = () => {
    const { gameId, versionId, fileId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [label, setLabel] = useState("");
    const [language, setLanguage] = useState("");
    const [file, setFile] = useState<File | null>(null);

    // Lekérjük az összes fájlt a verzióhoz
    const { data: allFiles, isLoading: isLoadingFiles } = useQuery({
        queryKey: ["translationFiles", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files?all=true`);
            if (!response.ok) {
                throw new Error("Fájlok lekérése sikertelen");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    const { data: translationFile, isLoading: isLoadingFile } = useQuery({
        queryKey: ["translationFile", gameId, versionId, fileId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!fileId,
    });

    const { data: uploadedZips, isLoading: isLoadingZips } = useQuery({
        queryKey: ["uploadedZips", gameId, versionId, fileId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}/uploaded_zips`);
            if (!response.ok) {
                throw new Error("Hálózati válasz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!fileId,
    });

    const { data: gameDetails, isLoading: isLoadingGame } = useQuery({
        queryKey: ["gameDetails", gameId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a játékhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId,
    });

    const { data: versionDetails, isLoading: isLoadingVersion } = useQuery({
        queryKey: ["versionDetails", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a verzióhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    const uploadMutation = useMutation({
        mutationFn: async (formData: FormData) => {
            const { token, param } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            if (param) {
                formData.append(param, token);
            }

            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}/uploaded_zips`, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                throw new Error("Fájl feltöltése sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["uploadedZips", gameId, versionId, fileId] });
            queryClient.invalidateQueries({ queryKey: ["translationFile", gameId, versionId, fileId] });
            setLabel("");
            setLanguage("");
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            toast({ title: "Sikeres feltöltés!", description: "A fájl sikeresen feltöltve." });
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    const unpackMutation = useMutation({
        mutationFn: async ({ zipId, accepted }: { zipId: string, accepted: boolean }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/uploaded_zips/${zipId}/unpack`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ accepted }),
            });
            if (!response.ok) {
                throw new Error("ZIP kicsomagolása sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["uploadedZips", gameId, versionId, fileId] });
            queryClient.invalidateQueries({ queryKey: ["translationFile", gameId, versionId, fileId] });
            toast({ title: "ZIP kicsomagolása sikeresen elindítva/befejezve!" });
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    const deleteZipMutation = useMutation({
        mutationFn: async (zipId: string) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/uploaded_zips/${zipId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("ZIP törlése sikertelen");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["uploadedZips", gameId, versionId, fileId] });
            toast({ title: "ZIP fájl sikeresen törölve!" });
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!file || !label || !language) {
            toast({
                title: "Hiba",
                description: "Kérjük, töltse ki az összes mezőt és válasszon fájlt.",
                variant: "destructive"
            });
            return;
        }
        const formData = new FormData();
        formData.append("uploaded_zip[label]", label);
        formData.append("uploaded_zip[language]", language);
        formData.append("uploaded_zip[file]", file);
        uploadMutation.mutate(formData);
    };

    const isLoading = isLoadingFile || isLoadingZips || isLoadingGame || isLoadingVersion;
    const isLoadingFileList = isLoadingFiles;

    return (
        <div className="container mx-auto p-4 space-y-8 bg-background text-foreground">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>{gameDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    {fileId && <BreadcrumbSeparator />}
                    {fileId && <BreadcrumbItem>Fájl: {translationFile?.name}</BreadcrumbItem>}
                </BreadcrumbList>
            </Breadcrumb>
            <VersionNavigation gameId={gameId} versionId={versionId} />

            {!fileId ? (
                // Fájl kiválasztás nézet
                isLoadingFileList ? (
                    <p>Fájlok betöltése...</p>
                ) : (
                    <Card>
                        <CardHeader>
                            <CardTitle>Fájl kezelés</CardTitle>
                            <CardDescription>Válassz egy fordítási fájlt a kezeléshez</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {allFiles && allFiles.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {[...allFiles]
                                        .sort((a: any, b: any) => naturalSort(a.name, b.name))
                                        .map((f: any) => (
                                            <Button
                                                key={f.id}
                                                onClick={() => navigate(`/games/${gameId}/versions/${versionId}/translation_files/${f.id}/file_management`)}
                                                variant="outline"
                                                className="h-auto py-4 px-3 whitespace-normal text-sm font-medium hover:bg-primary hover:text-primary-foreground"
                                            >
                                                {f.name}
                                            </Button>
                                        ))}
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-center py-8">Nincsenek elérhető fájlok ebben a verzióban.</p>
                            )}
                        </CardContent>
                    </Card>
                )
            ) : (
                // Fájl kezelés nézet
                isLoading ? (
                    <p>Betöltés...</p>
                ) : (
                    <Card>
                        <CardHeader>
                            <CardTitle>Fájl kezelés: {translationFile?.name}</CardTitle>
                            <CardDescription>ZIP fájlok feltöltése, kicsomagolása és kezelése</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Feltöltés szekció */}
                            <div className="border-b pb-6">
                                <h3 className="text-lg font-semibold mb-4">Új fájl feltöltése</h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <Label htmlFor="label">Címke</Label>
                                            <Input
                                                id="label"
                                                value={label}
                                                onChange={(e) => setLabel(e.target.value)}
                                                placeholder="pl. v1.0"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="language">Nyelv</Label>
                                            <select
                                                id="language"
                                                value={language}
                                                onChange={(e) => setLanguage(e.target.value)}
                                                className="w-full border rounded-md px-3 py-2 bg-background text-foreground"
                                            >
                                                <option value="">Válassz</option>
                                                <option value="EN">Angol (EN)</option>
                                                <option value="HU">Magyar (HU)</option>
                                                <option value="DE">Német (DE)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <Label htmlFor="file">Fájl</Label>
                                            <Input
                                                id="file"
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                                accept=".zip"
                                            />
                                        </div>
                                    </div>
                                    <Button type="submit" disabled={uploadMutation.isPending}>
                                        {uploadMutation.isPending ? "Feltöltés..." : "Feltöltés"}
                                    </Button>
                                </form>
                            </div>

                            {/* Feltöltött fájlok listája */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Feltöltött fájlok</h3>
                                {uploadedZips && uploadedZips.length > 0 ? (
                                    <div className="space-y-3">
                                        {uploadedZips.map((zip: any) => (
                                            <div key={zip.id} className="border rounded-lg p-4 flex items-center justify-between hover:bg-muted/50">
                                                <div className="flex-1">
                                                    <h4 className="font-semibold">{zip.label}</h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        Nyelv: {zip.language} | Státusz: {zip.status}
                                                    </p>
                                                    {zip.status === 'unpacking' && (
                                                        <div className="text-xs text-blue-600 mt-2">
                                                            ⏳ Kicsomagolás folyamatban...
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    {zip.status === 'uploaded' && (
                                                        <Button
                                                            onClick={() => unpackMutation.mutate({ zipId: zip.id, accepted: false })}
                                                            disabled={unpackMutation.isPending}
                                                            size="sm"
                                                        >
                                                            {unpackMutation.isPending ? "Kicsomagolás..." : "Kicsomagolás"}
                                                        </Button>
                                                    )}
                                                    {zip.status === 'unpacked' && (
                                                        <span className="text-sm text-gray-600">Kicsomagolva</span>
                                                    )}
                                                    <a
                                                        href={zip.file_url}
                                                        download
                                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-8 px-3 bg-blue-600 text-white hover:bg-blue-700"
                                                    >
                                                        Letöltés
                                                    </a>
                                                    <Button
                                                        onClick={() => {
                                                            if (window.confirm("Biztosan törölni szeretnéd ezt a fájlt?")) {
                                                                deleteZipMutation.mutate(zip.id);
                                                            }
                                                        }}
                                                        disabled={deleteZipMutation.isPending}
                                                        size="sm"
                                                        variant="destructive"
                                                    >
                                                        {deleteZipMutation.isPending ? "Törlés..." : "Törlés"}
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground">Nincsenek még feltöltött fájlok.</p>
                                )}
                            </div>

                            {/* Vissza gomb */}
                            <div className="border-t pt-4 mt-6">
                                <Button
                                    onClick={() => navigate(`/games/${gameId}/versions/${versionId}/file_management`)}
                                    variant="outline"
                                >
                                    ← Vissza a fájlok listájához
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )
            )}
        </div>
    );
};

export default Translate;
